import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Plus, X, Loader, AlertCircle, CheckCircle, Trash2, CalendarDays, User } from 'lucide-react';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm disabled:bg-gray-50';
const labelClass =
  'block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide';

const LEAVE_TYPE_LABELS = {
  casual: 'Casual Leave',
  sick: 'Sick Leave',
  earned: 'Earned/Annual Leave',
  other: 'Other',
};

const LEAVE_TYPE_COLORS = {
  casual: 'bg-blue-100 text-blue-700',
  sick: 'bg-red-100 text-red-700',
  earned: 'bg-green-100 text-green-700',
  other: 'bg-gray-100 text-gray-700',
};

const AddLeaveModal = ({ employees, onClose, onSaved }) => {
  const [employeeId, setEmployeeId] = useState('');
  const [date, setDate] = useState('');
  const [leaveType, setLeaveType] = useState('casual');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeId || !date) { setError('Employee and date are required.'); return; }
    setSaving(true); setError(null);
    try {
      await api.post('/employee-leaves/', { employee: employeeId, date, leave_type: leaveType, reason });
      onSaved();
    } catch (err) {
      const data = err.response?.data;
      if (data?.non_field_errors) setError(data.non_field_errors[0]);
      else setError(data?.detail || data?.error || 'Failed to save leave.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <CalendarDays className="text-amber-500" size={20} />
            <h2 className="text-base font-bold text-gray-900">Note a Leave</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
              <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />{error}
            </div>
          )}

          <div>
            <label className={labelClass}>Employee <span className="text-red-500">*</span></label>
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className={inputClass} required>
              <option value="">— Select employee —</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Date <span className="text-red-500">*</span></label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} required />
          </div>

          <div>
            <label className={labelClass}>Leave Type</label>
            <select value={leaveType} onChange={(e) => setLeaveType(e.target.value)} className={inputClass}>
              {Object.entries(LEAVE_TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Reason / Note</label>
            <textarea
              value={reason} onChange={(e) => setReason(e.target.value)}
              rows={2} placeholder="Optional reason..."
              className={inputClass + ' resize-none'} />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">
              {saving ? <Loader size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              Save Leave
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EmployeeLeaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Filters
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterType, setFilterType] = useState('');

  const loadLeaves = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (filterEmployee) params.employee = filterEmployee;
      if (filterFrom) params.date__gte = filterFrom;
      if (filterTo) params.date__lte = filterTo;
      if (filterType) params.leave_type = filterType;
      const res = await api.get('/employee-leaves/', { params });
      setLeaves(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      setError('Failed to load leaves.');
    } finally {
      setLoading(false);
    }
  }, [filterEmployee, filterFrom, filterTo, filterType]);

  const loadEmployees = useCallback(async () => {
    try {
      const res = await api.get('/employees/', { params: { page_size: 200, is_active: true } });
      setEmployees(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  useEffect(() => { loadLeaves(); }, [loadLeaves]);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this leave record?')) return;
    setDeleting(id);
    try {
      await api.delete(`/employee-leaves/${id}/`);
      setLeaves((prev) => prev.filter((l) => l.id !== id));
    } catch {
      alert('Failed to delete leave.');
    } finally {
      setDeleting(null);
    }
  };

  const resetFilters = () => {
    setFilterEmployee(''); setFilterFrom(''); setFilterTo(''); setFilterType('');
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="text-amber-500" size={22} /> Employee Leaves
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Approved leaves are excluded from salary deductions at payroll calculation.
          </p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={15} /> Note a Leave
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className={labelClass}>Employee</label>
            <select value={filterEmployee} onChange={(e) => setFilterEmployee(e.target.value)} className={inputClass}>
              <option value="">All employees</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>From</label>
            <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>To</label>
            <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Leave Type</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className={inputClass}>
              <option value="">All types</option>
              {Object.entries(LEAVE_TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-3 mt-3">
          <button onClick={loadLeaves}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
            Apply
          </button>
          <button onClick={resetFilters}
            className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50">
            Clear
          </button>
        </div>
      </div>

      {/* Content */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2 mb-4">
          <AlertCircle size={15} />{error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader size={24} className="animate-spin mr-2" /> Loading leaves...
        </div>
      ) : leaves.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <CalendarDays size={36} className="mx-auto mb-3 opacity-40" />
          <p className="font-medium">No leave records found</p>
          <p className="text-sm mt-1">Use the button above to note a leave for an employee.</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-400 mb-3">{leaves.length} record{leaves.length !== 1 ? 's' : ''}</p>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Employee</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Reason</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Noted By</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Delete</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leaves.map((lv) => (
                    <tr key={lv.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          <User size={14} className="text-gray-400 flex-shrink-0" />
                          {lv.employee_name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{lv.date}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${LEAVE_TYPE_COLORS[lv.leave_type] || 'bg-gray-100 text-gray-700'}`}>
                          {LEAVE_TYPE_LABELS[lv.leave_type] || lv.leave_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 max-w-[200px] truncate">{lv.reason || '—'}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{lv.noted_by_username || '—'}</td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => handleDelete(lv.id)} disabled={deleting === lv.id}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40">
                          {deleting === lv.id ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-gray-100">
              {leaves.map((lv) => (
                <div key={lv.id} className="p-4 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm">{lv.employee_name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{lv.date}</p>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${LEAVE_TYPE_COLORS[lv.leave_type] || 'bg-gray-100 text-gray-700'}`}>
                      {LEAVE_TYPE_LABELS[lv.leave_type] || lv.leave_type}
                    </span>
                    {lv.reason && <p className="text-xs text-gray-400 mt-1 truncate">{lv.reason}</p>}
                  </div>
                  <button onClick={() => handleDelete(lv.id)} disabled={deleting === lv.id}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-40">
                    {deleting === lv.id ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {showAdd && (
        <AddLeaveModal
          employees={employees}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); loadLeaves(); }}
        />
      )}
    </div>
  );
};

export default EmployeeLeaves;
